import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { Button } from '../components/shared/Button';
import { ServiceReviews } from '../components/shared/ServiceReviews'; // Importazione
import { InfiniteScrollReviews } from '../components/shared/InfiniteScrollReviews';
import { FreeTrialButton } from '../components/shared/FreeTrialButton';
import {
  ArrowLeft,
  Layers,
  CheckCircle2,
  Cpu,
  ShieldCheck,
  Smartphone,
  Zap,
  Briefcase,
  TrendingUp,
  Globe,
  LineChart
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES, EXTERNAL_LINKS, SERVICE_REVIEWS } from '../constants'; // Importazione Dati

export const CopyServicePage: React.FC = () => {
  const { t } = useLanguage();

  const steps = t('finance.services.copyService.steps') as any[];
  const benefits = t('finance.services.copyService.benefits') as any[];
  const logic = t('finance.services.copyService.logic') as any[];
  const partner = t('finance.services.copyService.partner') as any;

  // Icon mapping for logic items
  const logicIcons = [
    <Zap className="text-orange-500" size={24} />,
    <ShieldCheck className="text-green-500" size={24} />,
    <Globe className="text-blue-500" size={24} />,
    <LineChart className="text-purple-500" size={24} />
  ];

  return (
    <div className="bg-[#0B1120] text-white min-h-screen pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="mb-12">
          <Link to={ROUTES.FINANCE} className="flex items-center text-gray-400 hover:text-white transition-colors group">
            <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" />
            {t('common.back')}
          </Link>
        </div>

        {/* HERO SECTION */}
        <section className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="inline-block mb-6">
                <span className="bg-green-500/10 text-green-400 border border-green-500/20 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                  {t('finance.services.copyService.badge')}
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-6">
                CTC <span className="text-green-500">COPY SERVICE</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-10">
                {t('finance.services.copyService.detailedDesc')}
              </p>

              <div className="relative overflow-visible z-[100] mb-24">
                <FreeTrialButton
                  serviceName="CTC Copy Service"
                  buttonText={t('finance.services.copyService.btn')}
                />
              </div>


            </AnimatedSection>

            {/* PARTNER CARD */}
            <AnimatedSection delay={200}>
              <div className="bg-[#151E32] border border-gray-800 rounded-3xl p-8 relative overflow-hidden shadow-2xl group hover:border-blue-500 transition-colors z-0">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-[40px] pointer-events-none"></div>

                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center p-2 shadow-lg">
                    {/* Placeholder for FPG Logo - using text if image not available */}
                    <span className="text-red-600 font-black text-xl tracking-tighter">FPG</span>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{partner.name}</div>
                  </div>
                </div>

                <p className="text-gray-300 leading-relaxed mb-8">
                  {partner.desc}
                </p>

                <div className="space-y-3">


                  <a
                    href="https://www.myfxbook.com/members/ChartTowerCorp/ctc-copy-service/11830193"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-green-400 text-sm font-bold hover:text-green-300 transition-colors"
                  >
                    <CheckCircle2 size={16} />
                    <span>Monitora Performance Live su MyFxBook →</span>
                  </a>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* LOGIC & STRATEGY GRID */}
        <section className="mb-24">
          <AnimatedSection>
            <h2 className="text-3xl font-bold mb-10 text-center uppercase tracking-wide">
              <span className="text-green-500">LOGICA</span> OPERATIVA
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {logic.map((item: any, i: number) => (
                <div key={i} className="bg-[#151E32] border border-gray-800 rounded-2xl p-6 hover:border-gray-600 transition-colors h-full flex flex-col">
                  <div className="mb-4 p-3 bg-[#0B1120] rounded-xl w-fit border border-gray-700">
                    {logicIcons[i % logicIcons.length]}
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </section>

        {/* STEPS SECTION */}
        <section className="mb-24">
          <AnimatedSection>
            <h2 className="text-3xl font-bold mb-12 text-center uppercase tracking-wide">Come Iniziare</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {steps.map((step: any, i: number) => (
                <div key={i} className="bg-surface-dark border border-gray-800 rounded-2xl p-8 flex flex-col items-center text-center hover:border-green-500/30 transition-colors relative">
                  <div className="absolute top-4 right-4 text-green-500/10 font-black text-6xl select-none">
                    {i + 1}
                  </div>
                  <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 font-black text-xl mb-6 border border-green-500/20 relative z-10">
                    {i + 1}
                  </div>
                  <h4 className="font-bold text-white text-lg mb-3 relative z-10">{step.title}</h4>
                  <p className="text-sm text-gray-400 leading-relaxed relative z-10">{step.desc}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </section>

        {/* BENEFITS SECTION */}
        <section className="py-20 border-t border-gray-800 mb-20 bg-[#080C14] rounded-3xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 px-8">
            <div className="text-center">
              <ShieldCheck className="mx-auto text-green-500 mb-6" size={48} />
              <h3 className="text-xl font-bold mb-3 text-white">{benefits[0]?.title}</h3>
              <p className="text-gray-400 text-sm max-w-xs mx-auto">{benefits[0]?.desc}</p>
            </div>
            <div className="text-center">
              <Cpu className="mx-auto text-blue-500 mb-6" size={48} />
              <h3 className="text-xl font-bold mb-3 text-white">{benefits[1]?.title}</h3>
              <p className="text-gray-400 text-sm max-w-xs mx-auto">{benefits[1]?.desc}</p>
            </div>
            <div className="text-center">
              <CheckCircle2 className="mx-auto text-orange-500 mb-6" size={48} />
              <h3 className="text-xl font-bold mb-3 text-white">{benefits[2]?.title}</h3>
              <p className="text-gray-400 text-sm max-w-xs mx-auto">{benefits[2]?.desc}</p>
            </div>
          </div>
        </section>

        {/* RISULTATI E FEEDBACK */}
        <InfiniteScrollReviews
          service="Copy Service"
          autoScroll={true}
        />

      </div>
    </div>
  );
};
